/**
 * Λήψη παραγγελίας από το κινητό.
 *
 * Ροή: κατηγορία → (υποκατηγορία) → προϊόντα → σύνοψη → κουζίνα.
 * Πάνω, ΠΑΝΤΑ ορατός, πίνακας με όλες τις κατηγορίες (4 στήλες στο κινητό): αλλαγή κατηγορίας με
 * ένα πάτημα, χωρίς «πίσω» και χωρίς σύρσιμο (ιδιοκτήτης 7/10/2026 — η λωρίδα καρτελών ήθελε ψάξιμο
 * με το δάχτυλο). Κάτω τα προϊόντα της επιλεγμένης. Κάρτες και πλακίδια δείχνουν πόσα έχουν μπει
 * στη σύνοψη.
 *
 * Η σύνοψη είναι δεύτερη ΣΕΛΙΔΑ δίπλα στα προϊόντα: σύρσιμο αριστερά/δεξιά ή «Σύνοψη ›» / βέλος πίσω
 * (ιδιοκτήτης 7/10/2026: «να τη βλέπει οποιαδήποτε στιγμή και να επιστρέφει με swipe»). Στη θέση του
 * κουμπιού σύνοψης, μπάρα με το ΤΕΛΕΥΤΑΙΟ είδος: πάτημα = σημείωση σε αυτό, «−» = ένα λιγότερο.
 * Ό,τι δεν στάλθηκε φυλάγεται στη συσκευή (orderDraft) και επιστρέφει μετά από refresh.
 *
 * Πιάτα (course, μόνο με το dine_in_settings.course_management_enabled): ΔΕΝ διαλέγονται πριν το
 * είδος. Όλα μπαίνουν «Πρώτα» και ο σερβιτόρος αλλάζει στη σύνοψη μόνο όσα πάνε αργότερα, με ένα
 * πάτημα το καθένα (GENERIS 5/10/2026: «θέλουν να τα ρυθμίζουν κατά τη σύνοψη»).
 *
 * Επιλογές και «χωρίς» (CustomizeSheet): ΠΑΝΤΑ, χωρίς διακόπτη (απόφαση ιδιοκτήτη 6/10/2026 — ως
 * τότε dine_in_settings.waiter_customization_enabled). Στην πρώτη προσθήκη κάθε είδους φορτώνονται
 * οι ομάδες του· είδος χωρίς ομάδες και χωρίς υλικά «χωρίς» μπαίνει κατευθείαν, χωρίς φύλλο.
 *
 * Τιμή (Φ5 — «η τιμή ζει μόνο στο μέγεθος»): lineUnitPrice(είδος, μέγεθος, 'dine_in') + Σ επιλογών.
 * Επιλογή μεγέθους μόνο με >1 πωλήσιμα (dineInQuickAdd, ίδιος κανόνας με την κονσόλα ταμία), χωρίς
 * «Κανονικό». Ανοιχτή τιμή και ζυγιζόμενα (απόφαση Ε9α): τα πληκτρολόγια του ταμείου, γραμμή που δεν
 * ενώνεται ποτέ με άλλη (κλειδί -c<seq>), χωρίς επιλογές — το ποσό της δεν ξαναϋπολογίζεται ποτέ.
 */

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Search, X, ArrowUp, ClipboardList, Loader2, Check, Trash2, Package, MessageSquare, StickyNote, Settings,
  Minus, SlidersHorizontal, Ban,
} from 'lucide-react';
import { useToast } from '../../shared/Toast';
import OpenPriceModal from '../../shared/OpenPriceModal';
import WeightModal from '../../shared/WeightModal';
import { dineInApi, DineInApiError } from '../../../services/dineInApi';
import {
  productDisplayPrice, defaultSize, lineUnitPrice, weightedLineTotal,
} from '../../../utils/pricing';
import { dineInQuickAdd } from '../../../utils/dineInQuickAdd';
import { showSizeName } from '../../../utils/sellableSizes';
import { roundToCents, unitPriceWithAddOns } from '../../../utils/dineInMoney';
import { removableSimpleIngredients } from '../../../utils/simpleIngredients';
import { catalogTone } from '../../../utils/catalogColors';
import type { Product, ProductSize, Category, PosOptionGroup, SelectedOptionEntry, SimpleIngredient } from '../../../types/order';
import { FullScreen, Sheet, Button, IconButton, Stepper, EmptyState, money } from './ui';
import CustomizeSheet, {
  CustomizeResult, EditScope, RemovedIngredient, defaultSelections, surcharge,
} from './CustomizeSheet';
import KitchenMessageSheet from './KitchenMessageSheet';
import DeviceSettingsSheet from './DeviceSettingsSheet';
import SwipeToDelete from './SwipeToDelete';
import {
  CardSize, CATEGORY_CARD, CATEGORY_GRID, PRODUCT_CARD, PRODUCT_GRID, PRODUCT_ROW, useWaiterPrefs,
} from './devicePrefs';
import { clearOrderDraft, readOrderDraft, writeOrderDraft } from './orderDraft';

export interface CartLine {
  /** Ταυτότητα της γραμμής στην οθόνη (άνοιγμα, σημείωση) — δεν στέλνεται. */
  uid: number;
  key: string;
  product_id: number;
  size_id: number | null;
  product_name: string;
  size_name: string | null;
  unit_price: number;
  quantity: number;
  notes: string | null;
  course_number: number;
  /** Από το φύλλο επιλογών (CustomizeSheet)· κενά για είδος χωρίς επιλογές/«χωρίς». */
  selected_options: SelectedOptionEntry[];
  removed: RemovedIngredient[];
  /** Σειρά της τελευταίας προσθήκης στη γραμμή — η μεγαλύτερη = το «τελευταίο είδος» της μπάρας. */
  touched: number;
  /** Ζυγιζόμενο (Ε9α): βάρος της γραμμής· unit_price = τιμή κιλού × βάρος, ποσότητα πάντα 1. */
  weight?: number | null;
  weight_unit?: string | null;
  /** Ανοιχτή τιμή (Ε9α): unit_price = το ποσό που πληκτρολόγησε ο σερβιτόρος. */
  is_open_price?: boolean;
}

/** Γραμμή ανοιχτής τιμής ή ζυγίσματος: το ποσό της ΔΕΝ βγαίνει από τον κατάλογο, άρα δεν ξαναϋπολογίζεται
 *  (καμία αλλαγή επιλογών) και δεν ενώνεται ποτέ με άλλη. */
const isCustomLine = (line: Pick<CartLine, 'weight' | 'is_open_price'>): boolean =>
  line.weight != null || line.is_open_price === true;

/** Κλειδί γραμμής ανοιχτής τιμής/ζυγίσματος: μοναδικό με τον αύξοντα -c<seq>. */
const customLineKey = (base: string, seq: number): string => `${base}-c${seq}`;

/** Ο μεγαλύτερος αύξων -c<seq> στα κλειδιά (π.χ. γραμμές από πρόχειρο): οι νέες γραμμές ανοιχτής
 *  τιμής/ζυγίσματος συνεχίζουν από εκεί, ώστε κανένα κλειδί να μη συμπέσει με γραμμή που ξαναφορτώθηκε. */
const maxCustomSeq = (lines?: Array<{ key: string }> | null): number =>
  (lines ?? []).reduce((max, l) => {
    const m = /-c(\d+)$/.exec(l.key);
    return m ? Math.max(max, Number(m[1])) : max;
  }, 0);

/** Τιμή κιλού/λίτρου dine-in ζυγιζόμενου (απόφαση Ε9α): η τιμή του προεπιλεγμένου μεγέθους. */
const weightPricePerUnit = (product: Product): number =>
  lineUnitPrice(product, defaultSize(product.sizes), 'dine_in');

const weightUnitLabel = (product: Product) => (product.selling_unit === 'LT' ? 'lt' : 'kg');

/** «0.5 kg» μιας γραμμής ζυγίσματος (σύνοψη, μπάρα). */
const lineWeightLabel = (line: CartLine): string =>
  `${line.weight} ${(line.weight_unit || 'KG').toLowerCase()}`;

/** Η τιμή στο πλακίδιο του είδους. */
const tilePrice = (product: Product): string =>
  product.is_open_price
    ? 'Ανοιχτή τιμή'
    : product.is_weight_variable
      ? `${money(weightPricePerUnit(product))}/${weightUnitLabel(product)}`
      : money(productDisplayPrice(product, 'dine_in'));

// Τα modals του ταμείου έχουν z-50, ενώ οι οθόνες του app (FullScreen/Sheet) είναι portal στο
// body με z-[100]: χωρίς αυτό το στρώμα το πληκτρολόγιο θα άνοιγε ΚΑΤΩ από την οθόνη παραγγελίας.
const AboveScreens: React.FC<{ children: React.ReactNode }> = ({ children }) =>
  createPortal(
    <div className="role-workspace role-waiter" style={{ position: 'fixed', inset: 0, zIndex: 110 }}>
      {children}
    </div>,
    document.body
  );

interface OrderScreenProps {
  storeId: number;
  table: any;
  sessionId: number | null;
  guestCount: number;
  categories: Category[];
  products: Product[];
  /** «Πιάτα ημέρας» (migration 278): της βιβλιοθήκης που ΔΕΝ είναι σήμερα — δεν φαίνονται στα προϊόντα ούτε στην
   *  αναζήτηση. Τα `products` μένουν πλήρη για τις γραμμές του καλαθιού (αλλαγή επιλογών, «χωρίς»). */
  hiddenProductIds?: ReadonlySet<number>;
  coursesEnabled: boolean;
  /** Τα έτοιμα μηνύματα κουζίνας· null = κλειστή λειτουργία: ίδια σύνοψη και ίδιο payload με πριν. */
  kitchenMessages?: string[] | null;
  onClose: () => void;
  onSent: () => void;
  /** Ο server έκοψε τον γύρο ως «κάτω από τον κατάλογο»: ο γονέας ξαναφορτώνει τον κατάλογο. */
  onCatalogStale?: () => void;
}

export const STALE_PRICES_MESSAGE =
  'Οι τιμές του καταλόγου άλλαξαν — ο κατάλογος ανανεώθηκε. Αφαιρέστε και ξαναπροσθέστε τα είδη.';

/** Τα πιάτα του PDA. Ήρεμο το «Πρώτα» (εκεί μπαίνουν όλα), έντονα όσα πάνε αργότερα — με μια
 *  ματιά φαίνεται τι θα περιμένει «ΠΑΣΟ». Το ταμείο φτάνει ως το 5ο· στο κινητό αρκούν τρία
 *  (GENERIS 5/10/2026: 37 γραμμές 1ο, 23 2ο, 2 3ο). */
export const COURSES = [
  { label: 'Πρώτα', pill: 'border-2 border-line-strong text-t2' },
  { label: 'Δεύτερα', pill: 'bg-amber-400 text-amber-950' },
  { label: 'Τρίτα', pill: 'bg-violet-600 text-white' },
] as const;

const FIRST_COURSE = 1;

/** Επόμενο πιάτο στο πάτημα: Πρώτα → Δεύτερα → Τρίτα → Πρώτα. */
export const nextCourse = (course: number): number => (course >= COURSES.length ? FIRST_COURSE : course + 1);

const courseOf = (course: number): { label: string; pill: string } =>
  COURSES[course - 1] ?? { label: `Πιάτο ${course}`, pill: 'border-2 border-line-strong text-t2' };

/** Κλειδί γραμμής: ίδιο είδος/μέγεθος ΚΑΙ ίδιες επιλογές/«χωρίς» — γραμμές με άλλες επιλογές ΔΕΝ
 *  ενώνονται. Το πιάτο ΔΕΝ είναι μέσα: αλλάζει στη σύνοψη και συγκρίνεται χωριστά (sameLine), ώστε
 *  το κλειδί να μη χρειάζεται ποτέ ξαναγράψιμο — μια γραμμή με μοναδικό κλειδί μένει μοναδική. */
const lineKey = (
  productId: number,
  sizeId: number | null,
  options: SelectedOptionEntry[],
  removed: RemovedIngredient[]
): string => {
  const plain = `${productId}-${sizeId ?? 'x'}`;
  if (options.length === 0 && removed.length === 0) return plain;
  const o = options.map((x) => `${x.group_id}.${x.choice_id}`).sort().join(',');
  const r = removed.map((x) => x.id).sort((a, b) => a - b).join(',');
  return `${plain}-o${o}-r${r}`;
};

interface MergeableLine {
  key: string;
  course_number: number;
  unit_price: number;
  quantity: number;
  notes: string | null;
}

/** Δύο γραμμές είναι «η ίδια» μόνο με ίδιο κλειδί, ίδιο πιάτο, ίδια τιμή μονάδας και καμία σημείωση —
 *  αλλιώς η ένωση θα χρέωνε τη μία με την τιμή της άλλης ή θα έχανε ένα σχόλιο. */
const sameLine = (a: MergeableLine, b: MergeableLine): boolean =>
  !a.notes && !b.notes && a.key === b.key && a.course_number === b.course_number && a.unit_price === b.unit_price;

/** Πριν την αποστολή: ίδιες γραμμές (sameLine) γίνονται μία. Στη σύνοψη ΔΕΝ ενώνονται (θα «χάνονταν»
 *  κάτω από το δάχτυλο), αλλά μια γραμμή που άλλαξε πιάτο μπορεί να συμπέσει με άλλη — στο δελτίο
 *  κουζίνας «2x», όχι δύο φορές «1x». Η σειρά μένει της πρώτης· τα αρχικά αντικείμενα δεν αλλάζουν. */
export function mergeCartLines<T extends MergeableLine>(lines: T[]): T[] {
  const out: T[] = [];
  for (const line of lines) {
    const twin = out.find((l) => sameLine(l, line));
    if (twin) twin.quantity += line.quantity;
    else out.push({ ...line });
  }
  return out;
}

/** Γραμμές από πρόχειρο (orderDraft — refresh, κλείσιμο εφαρμογής): μόνο αν είναι ΟΛΕΣ έγκυρες· μισή
 *  παραγγελία είναι χειρότερη από καμία. Νέα uid, η σειρά των προσθηκών (touched) μένει. */
export function restoreDraftLines(raw: unknown[]): CartLine[] | null {
  const valid = (l: any): boolean =>
    !!l && typeof l.key === 'string' && Number.isInteger(l.product_id)
    && (l.size_id === null || Number.isInteger(l.size_id)) && typeof l.product_name === 'string'
    && (l.size_name === null || typeof l.size_name === 'string')
    && Number.isFinite(l.unit_price) && l.unit_price >= 0
    && Number.isInteger(l.quantity) && l.quantity >= 1
    && (l.notes === null || typeof l.notes === 'string')
    && Number.isInteger(l.course_number) && l.course_number >= 1
    && Array.isArray(l.selected_options) && Array.isArray(l.removed) && Number.isFinite(l.touched)
    // Ζύγισμα/ανοιχτή τιμή (Φ5): ταξιδεύουν μέσα στη γραμμή. Ζυγισμένη γραμμή = ένα ποσό για ένα βάρος,
    // άρα πάντα ποσότητα 1 (×2 θα χρέωνε διπλό το ζυγισμένο).
    && (l.weight == null || (Number.isFinite(l.weight) && l.weight > 0 && l.quantity === 1))
    && (l.weight_unit == null || typeof l.weight_unit === 'string')
    && (l.is_open_price === undefined || typeof l.is_open_price === 'boolean');
  if (raw.length === 0 || !raw.every(valid)) return null;
  const lines = raw as CartLine[];
  const byTouch = [...lines].sort((a, b) => a.touched - b.touched);
  return lines.map((l, i) => ({ ...l, uid: i + 1, touched: byTouch.indexOf(l) + 1 }));
}

/** Υποκατηγορίες που βλέπει ο σερβιτόρος (οι κρυμμένες μένουν μόνο στη διαχείριση). */
const visibleSubcategories = (category: Category | null): Category[] =>
  (category?.subcategories ?? []).filter((c) => !c.is_hidden);

/** Γράμματα κάρτας κατηγορίας ΠΡΙΝ μετρηθεί η κάρτα (και όπου δεν υπάρχει canvas): στις 4 στήλες
 *  των 360px χωρούν ~9 έντονα κεφαλαία στα 12px (≈0,65em ανά γράμμα). Μεγάλη λέξη → μικρότερα. */
export function categoryNameClass(name: string): string {
  const longest = Math.max(0, ...name.split(/\s+/).map((word) => word.length));
  return longest > 10 ? 'text-[10px]' : longest > 9 ? 'text-[11px]' : 'text-[12px]';
}

const MIN_FIT_PX = 9;
let measureContext: CanvasRenderingContext2D | null | undefined;

/** Το μεγαλύτερο μέγεθος (maxPx→9px) όπου ΚΑΘΕ λέξη χωρά ολόκληρη στο πλάτος — μετρημένο με τη
 *  γραμματοσειρά της συσκευής (Roboto στο Android ≠ Segoe UI στα Windows). null = δεν μετριέται. */
export function fitCategoryFont(text: string, width: number, fontWeight: string, fontFamily: string, maxPx = 12): number | null {
  if (!(width > 0)) return null;
  if (measureContext === undefined) {
    try {
      // jsdom (tests) δεν έχει canvas — εκεί μένει ο κανόνας του categoryNameClass.
      measureContext = typeof document === 'undefined' || /jsdom/i.test(navigator.userAgent)
        ? null
        : document.createElement('canvas').getContext('2d');
    } catch {
      measureContext = null;
    }
  }
  const ctx = measureContext;
  if (!ctx) return null;
  const words = text.split(/\s+/).filter(Boolean);
  const fits = (word: string) => ctx.measureText(word).width <= width;
  for (let px = Math.max(MIN_FIT_PX, Math.round(maxPx)); px >= MIN_FIT_PX; px -= 1) {
    ctx.font = `${fontWeight} ${px}px ${fontFamily}`;
    if (words.every(fits)) return px;
  }
  return MIN_FIT_PX;
}

/** Κάρτα του πίνακα κατηγοριών. Η ενεργή γεμάτη· ο μετρητής = είδη της κατηγορίας στη σύνοψη. */
const CategoryCard: React.FC<{
  category: Category;
  active: boolean;
  inCart: number;
  size: CardSize;
  onSelect: (category: Category) => void;
}> = ({ category, active, inCart, size, onSelect }) => {
  const { minH, maxFont } = CATEGORY_CARD[size];
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [fontPx, setFontPx] = useState<number | null>(null);
  useLayoutEffect(() => {
    const button = buttonRef.current;
    if (!button) return;
    const fit = () => {
      const style = getComputedStyle(button);
      const width = button.clientWidth - parseFloat(style.paddingLeft || '0') - parseFloat(style.paddingRight || '0');
      setFontPx(fitCategoryFont(category.name, width, style.fontWeight || '700', style.fontFamily || 'sans-serif', maxFont));
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
    // Αλλαγή στηλών από τις ρυθμίσεις = νέο πλάτος: ο πίνακας ξαναστήνεται (key) και μετρά από την αρχή.
  }, [category.name, maxFont]);
  return (
    <button
      ref={buttonRef}
      type="button"
      aria-pressed={active}
      // Τόνος που όρισε ο διαχειριστής (category.color): απαλή απόχρωση + λωρίδα (RoleV2.css §3)·
      // χωρίς χρώμα η κάρτα μένει ουδέτερη όπως πριν.
      data-tone={catalogTone(category.color) ?? undefined}
      onClick={() => onSelect(category)}
      className={`role-choice waiter-category relative ${minH} px-0.5 py-1 rounded-lg border font-bold flex items-center justify-center text-center transition-transform active:scale-[0.97] ${
        active ? 'bg-blue-600 border-blue-600 text-white' : 'bg-surface-1 border-line text-t1'
      }`}
    >
      <span
        style={fontPx ? { fontSize: `${fontPx}px` } : undefined}
        className={`leading-[1.15] line-clamp-2 break-words max-w-full ${fontPx ? '' : categoryNameClass(category.name)}`}
      >
        {category.name}
      </span>
      {inCart > 0 && (
        <span
          aria-hidden="true"
          data-testid={`category-in-cart-${category.id}`}
          className={`absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold leading-4 tabular-nums ${
            active ? 'bg-white text-blue-700' : 'bg-blue-600 text-white'
          }`}
        >
          {inCart}
        </span>
      )}
    </button>
  );
};

interface Customizing {
  product: Product;
  size: ProductSize | null;
  groups: PosOptionGroup[];
  removable: SimpleIngredient[];
  /** Προσθήκη: όλες οι ομάδες του είδους (για τη σειρά των επιλογών) και οι προεπιλογές των
   *  προαιρετικών, που μπαίνουν χωρίς να φανούν (βλ. openCustomization). */
  allGroups?: PosOptionGroup[];
  extraOptions?: SelectedOptionEntry[];
  /** Αλλαγή υπάρχουσας γραμμής (μπάρα/σύνοψη): ποια, τι αλλάζει, και τι έχει τώρα. */
  edit?: {
    uid: number;
    part: 'options' | 'removed';
    quantity: number;
    options: SelectedOptionEntry[];
    removed: number[];
    /** Γραμμή ανοιχτής τιμής/ζυγίσματος: το ποσό της, που ΔΕΝ ξαναϋπολογίζεται από τον κατάλογο. */
    fixedUnitPrice?: number;
  };
}

/** Ομάδες που μπορούν να φανούν: μια ομάδα χωρίς επιλογές θα κλείδωνε την προσθήκη αν ήταν υποχρεωτική. */
const usableGroups = (groups?: PosOptionGroup[] | null): PosOptionGroup[] =>
  (groups ?? []).filter((g) => Array.isArray(g?.choices) && g.choices.length > 0);

/** Επιλογές στη σειρά των ομάδων και των επιλογών τους (όπως στο φύλλο και στο δελτίο κουζίνας). */
const inGroupOrder = (groups: PosOptionGroup[], options: SelectedOptionEntry[]): SelectedOptionEntry[] =>
  groups.flatMap((g) => g.choices.flatMap((c) => options.filter((o) => o.group_id === g.id && o.choice_id === c.id)));

/** Κουμπιά αλλαγής ενός είδους — στη μπάρα (τελευταίο είδος) και στη γραμμή της σύνοψης: επιλογές
 *  (με το όνομα της ομάδας όταν είναι μία, π.χ. «ΖΥΜΑΡΙΚΟ»), «Χωρίς», σημείωση· μόνο όσα έχει το είδος. */
const EditChips: React.FC<{
  optionsLabel: string | null;
  canRemove: boolean;
  hasNote?: boolean;
  onOptions: () => void;
  onRemoved: () => void;
  /** Χωρίς αυτό δεν φαίνεται κουμπί σημείωσης (η γραμμή της σύνοψης έχει δικό της). */
  onNote?: () => void;
}> = ({ optionsLabel, canRemove, hasNote, onOptions, onRemoved, onNote }) => {
  // flex-auto (όχι ίσα πλάτη): κάθε κουμπί όσο το κείμενό του — «ΖΥΜΑΡΙΚΟ» χωρά, ο χώρος που μένει μοιράζεται.
  const chip =
    'flex-auto min-w-0 min-h-[44px] px-2 rounded-xl bg-surface-2 border border-line-strong dark:border-transparent text-t1 text-sm font-bold flex items-center justify-center gap-1.5 active:bg-surface-3';
  return (
    <div className="flex items-center gap-1.5">
      {optionsLabel && (
        <button type="button" onClick={onOptions} className={chip}>
          <SlidersHorizontal className="w-4 h-4 shrink-0 text-blue-600 dark:text-blue-400" />
          <span className="truncate">{optionsLabel}</span>
        </button>
      )}
      {canRemove && (
        <button type="button" onClick={onRemoved} className={chip}>
          <Ban className="w-4 h-4 shrink-0 text-red-600 dark:text-red-400" />
          <span className="truncate">Χωρίς</span>
        </button>
      )}
      {onNote && (
        <button type="button" onClick={onNote} className={chip}>
          <StickyNote className={`w-4 h-4 shrink-0 ${hasNote ? 'text-amber-600 dark:text-amber-400' : 'text-t3'}`} />
          <span className="truncate">Σημείωση</span>
        </button>
      )}
    </div>
  );
};

/** Λωρίδα καρτελών (κατηγορίες ή υποκατηγορίες): ένα πάτημα για αλλαγή· η ενεργή μένει ορατή. */
const ChipStrip: React.FC<{
  label: string;
  items: Category[];
  activeId: number | null;
  onSelect: (item: Category) => void;
}> = ({ label, items, activeId, onSelect }) => {
  const stripRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const strip = stripRef.current;
    const active = strip?.querySelector<HTMLElement>('[aria-pressed="true"]');
    if (!strip || !active) return;
    // Η ενεργή στο κέντρο, κυλώντας ΜΟΝΟ τη λωρίδα — το scrollIntoView θα κουνούσε και τις σελίδες
    // της οθόνης (προϊόντα ↔ σύνοψη).
    const s = strip.getBoundingClientRect();
    const a = active.getBoundingClientRect();
    strip.scrollLeft += a.left + a.width / 2 - (s.left + s.width / 2);
  }, [activeId]);
  return (
    <div
      ref={stripRef}
      role="group"
      aria-label={label}
      className="flex-1 min-w-0 flex gap-1.5 overflow-x-auto overscroll-x-contain wa-no-scrollbar"
    >
      {items.map((item) => {
        const active = item.id === activeId;
        return (
          <button
            key={item.id}
            type="button"
            aria-pressed={active}
            onClick={() => onSelect(item)}
            className={`role-choice shrink-0 px-3.5 min-h-[44px] rounded-full text-sm font-bold whitespace-nowrap ${
              active ? 'bg-blue-600 text-white' : 'bg-surface-1 text-t2 active:bg-surface-2'
            }`}
          >
            {item.name}
          </button>
        );
      })}
    </div>
  );
};

interface SummaryLineProps {
  line: CartLine;
  showSize: boolean;
  coursesEnabled: boolean;
  expanded: boolean;
  /** Κουμπιά επιλογών/«χωρίς» της ανοιχτής γραμμής (EditChips) — null/false = δεν έχει. */
  optionsLabel: string | null;
  canRemove: boolean;
  onToggle: () => void;
  onCourse: () => void;
  onQuantity: (delta: number) => void;
  onNote: () => void;
  onOptions: () => void;
  onRemoved: () => void;
  onRemove: () => void;
  /** Σύρσιμο αριστερά: διαγραφή με «Αναίρεση». */
  onSwipeDelete: () => void;
}

/** Μία γραμμή της σύνοψης: ό,τι χρειάζεται για επιβεβαίωση σε μία σειρά. Ποσότητα, σημείωση,
 *  επιλογές/«χωρίς» και διαγραφή εμφανίζονται μόνο με πάτημα στη γραμμή — αλλιώς κάθε είδος έπιανε
 *  μισή οθόνη. Γρήγορη διαγραφή: σύρσιμο αριστερά. */
const SummaryLine: React.FC<SummaryLineProps> = ({
  line, showSize, coursesEnabled, expanded, optionsLabel, canRemove,
  onToggle, onCourse, onQuantity, onNote, onOptions, onRemoved, onRemove, onSwipeDelete,
}) => {
  const course = courseOf(line.course_number);
  return (
    <li data-testid="summary-line">
      {/* Σύρσιμο αριστερά = γρήγορη διαγραφή (με «Αναίρεση» στη σύνοψη)· δεξιά = πίσω στα προϊόντα. */}
      <SwipeToDelete onDelete={onSwipeDelete} className={expanded ? 'bg-surface-1' : 'bg-surface-0'}>
        <div className="flex items-stretch">
          <button
            type="button"
            aria-expanded={expanded}
            onClick={onToggle}
            className="flex-1 min-w-0 min-h-[52px] pl-3 pr-2 py-2 flex items-start gap-2 text-left active:bg-surface-2"
          >
            <div className="min-w-0 flex-1">
              <p className="text-[15px] font-bold text-t1 leading-snug break-words">
                <span className="tabular-nums">{line.quantity}×</span> {line.product_name}
                {showSize && <span className="font-semibold text-blue-600 dark:text-blue-400"> · {line.size_name}</span>}
                {line.weight != null && (
                  <span className="font-semibold text-amber-700 dark:text-amber-400 tabular-nums"> · {lineWeightLabel(line)}</span>
                )}
              </p>
              {line.selected_options.length > 0 && (
                <p className="text-[13px] text-t2 leading-snug">
                  {line.selected_options
                    .map((o) => (o.price_modifier > 0 ? `${o.choice_name} ${surcharge(o.price_modifier)}` : o.choice_name))
                    .join(' · ')}
                </p>
              )}
              {line.removed.length > 0 && (
                <p className="text-[13px] font-semibold text-red-600 dark:text-red-400 leading-snug">
                  χωρίς {line.removed.map((r) => r.name).join(', ')}
                </p>
              )}
              {line.notes && <p className="text-[13px] text-amber-700 dark:text-amber-400 leading-snug">📝 {line.notes}</p>}
            </div>
            <span className="shrink-0 text-[15px] font-bold text-t1 tabular-nums leading-snug">
              {money(line.unit_price * line.quantity)}
            </span>
          </button>
          {coursesEnabled && (
            <button
              type="button"
              onClick={onCourse}
              aria-label={`Πιάτο: ${course.label}`}
              title="Πάτα για άλλο πιάτο"
              className="shrink-0 min-h-[52px] pl-1 pr-3 flex items-center"
            >
              <span className={`w-[78px] h-8 rounded-full text-[13px] font-bold flex items-center justify-center ${course.pill}`}>
                {course.label}
              </span>
            </button>
          )}
        </div>
        {expanded && (
          <div className="px-3 pb-2 space-y-1.5">
            <div className="flex items-center gap-2">
              {/* Ζύγισμα = ένα ποσό για ένα βάρος: ποσότητα 2 θα το χρέωνε διπλό. Όπως το καλάθι του
                  ταμείου, χωρίς +/− — άλλο βάρος = νέα γραμμή. */}
              {line.weight == null && (
                <Stepper value={line.quantity} min={1} onDecrease={() => onQuantity(-1)} onIncrease={() => onQuantity(1)} />
              )}
              <button
                type="button"
                onClick={onNote}
                className="flex-1 min-w-0 min-h-[44px] px-2 rounded-xl bg-surface-2 border border-line-strong dark:border-transparent text-t2 text-sm font-semibold flex items-center justify-center gap-1.5 active:bg-surface-3"
              >
                <StickyNote className="w-4 h-4 shrink-0" />
                <span className="truncate">{line.notes ? 'Αλλαγή σημείωσης' : 'Σημείωση'}</span>
              </button>
              <IconButton label="Αφαίρεση είδους" onClick={onRemove}>
                <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
              </IconButton>
            </div>
            {(optionsLabel || canRemove) && (
              <EditChips optionsLabel={optionsLabel} canRemove={canRemove} onOptions={onOptions} onRemoved={onRemoved} />
            )}
          </div>
        )}
      </SwipeToDelete>
    </li>
  );
};

/** Κάτω μπάρα των προϊόντων: το τελευταίο είδος που μπήκε, με ό,τι έχει (επιλογές, «χωρίς», σημείωση)
 *  και κουμπιά για να τα αλλάξεις (ιδιοκτήτης 7/10/2026: στην προσθήκη μόνο τα υποχρεωτικά — «έξτρα, να
 *  αφαιρέσει και οι σημειώσεις σαν επιλογές κάτω στη μπάρα»). «−» = ένα λιγότερο (π.χ. λάθος πάτημα).
 *  Κουμπί σύνοψης ΔΕΝ υπάρχει: η σύνοψη είναι σύρσιμο, πλήθος και σύνολο στην κεφαλίδα. */
const LastItemBar: React.FC<{
  line: CartLine | null;
  showSize: boolean;
  optionsLabel: string | null;
  canRemove: boolean;
  onOptions: () => void;
  onRemoved: () => void;
  onNote: () => void;
  onRemoveOne: () => void;
}> = ({ line, showSize, optionsLabel, canRemove, onOptions, onRemoved, onNote, onRemoveOne }) => {
  if (!line) {
    return (
      <p className="pb-1 min-h-[48px] px-1 flex items-center text-sm text-t4 leading-tight">
        Πάτα ένα προϊόν για να μπει στη σύνοψη
      </p>
    );
  }
  const options = line.selected_options
    .map((o) => (o.price_modifier > 0 ? `${o.choice_name} ${surcharge(o.price_modifier)}` : o.choice_name))
    .join(' · ');
  const hasModifiers = !!options || line.removed.length > 0 || !!line.notes;
  return (
    <div className="pb-1 space-y-1.5">
      <div className="flex items-center gap-1.5">
        {/* Νέο key σε κάθε προσθήκη = μια λάμψη: φαίνεται τι μπήκε χωρίς να κοιτάξεις το πλακίδιο. */}
        <div key={line.touched} data-testid="last-item" className="wa-flash flex-1 min-w-0 px-1.5 py-0.5 rounded-lg">
          <p className="text-[15px] font-bold text-t1 leading-tight truncate">
            {line.quantity}× {line.product_name}
            {showSize && <span className="font-semibold text-blue-600 dark:text-blue-400"> · {line.size_name}</span>}
            {line.weight != null && (
              <span className="font-semibold text-amber-700 dark:text-amber-400 tabular-nums"> · {lineWeightLabel(line)}</span>
            )}
          </p>
          <p className="text-[13px] leading-tight truncate">
            {hasModifiers ? (
              <>
                {options && <span className="text-t2">{options}</span>}
                {line.removed.length > 0 && (
                  <span className="font-semibold text-red-600 dark:text-red-400">
                    {options ? ' · ' : ''}χωρίς {line.removed.map((r) => r.name).join(', ')}
                  </span>
                )}
                {line.notes && (
                  <span className="font-semibold text-amber-700 dark:text-amber-400">
                    {options || line.removed.length > 0 ? ' · ' : ''}📝 {line.notes}
                  </span>
                )}
              </>
            ) : (
              <span className="text-t4">σύρε ← για σύνοψη</span>
            )}
          </p>
        </div>
        <IconButton
          label="Αφαίρεση ενός"
          onClick={onRemoveOne}
          className="bg-surface-2 border border-line-strong dark:border-transparent active:bg-surface-3"
        >
          <Minus className="w-5 h-5" />
        </IconButton>
      </div>
      <EditChips
        optionsLabel={optionsLabel}
        canRemove={canRemove}
        hasNote={!!line.notes}
        onOptions={onOptions}
        onRemoved={onRemoved}
        onNote={onNote}
      />
    </div>
  );
};

/** Σελίδες της οθόνης παραγγελίας: 0 = προϊόντα, 1 = σύνοψη. */
type Page = 0 | 1;

const OrderScreen: React.FC<OrderScreenProps> = ({
  storeId,
  table,
  sessionId,
  guestCount,
  categories,
  products,
  hiddenProductIds,
  coursesEnabled,
  kitchenMessages = null,
  onClose,
  onSent,
  onCatalogStale,
}) => {
  const { showToast } = useToast();
  const [category, setCategory] = useState<Category | null>(null);
  const [subcategory, setSubcategory] = useState<Category | null>(null);
  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  // Πρόχειρο που δεν στάλθηκε (refresh, κλείσιμο εφαρμογής, «πίσω» του κινητού…): συνεχίζει από εκεί.
  // Τα μηνύματα κουζίνας μόνο αν η λειτουργία είναι ακόμη ανοιχτή — αλλιώς θα έφευγαν σε κλειστή.
  const [restored] = useState(() => {
    const draft = readOrderDraft(storeId, table.id, sessionId);
    const lines = draft ? restoreDraftLines(draft.lines) : null;
    return draft && lines ? { lines, messages: kitchenMessages ? draft.messages : [], savedAt: draft.savedAt } : null;
  });
  const [cart, setCart] = useState<CartLine[]>(() => restored?.lines ?? []);
  const [page, setPage] = useState<Page>(0);
  const [expandedUid, setExpandedUid] = useState<number | null>(null);
  const [sizePicker, setSizePicker] = useState<Product | null>(null);
  // Ανοιχτή τιμή / ζύγισμα (Ε9α): το είδος του ανοιχτού πληκτρολογίου.
  const [openPriceFor, setOpenPriceFor] = useState<Product | null>(null);
  const [weightFor, setWeightFor] = useState<Product | null>(null);
  const customLineSeq = useRef(maxCustomSeq(restored?.lines));
  const [notesFor, setNotesFor] = useState<CartLine | null>(null);
  const [notesDraft, setNotesDraft] = useState('');
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);
  // Μηνύματα κουζίνας μαζί με την παραγγελία («ΟΛΑ ΜΑΖΙ» …) — στην κορυφή του δελτίου.
  const [orderMessages, setOrderMessages] = useState<string[]>(() => restored?.messages ?? []);
  const [messageSheet, setMessageSheet] = useState(false);
  // Επιλογές/«χωρίς»: το είδος που φορτώνει και το ανοιχτό φύλλο.
  const [customizing, setCustomizing] = useState<Customizing | null>(null);
  const [loadingProductId, setLoadingProductId] = useState<number | null>(null);
  // Ομάδες ανά είδος για όσο μένει ανοιχτή η οθόνη: δεύτερο πάτημα = χωρίς αναμονή δικτύου. Όταν
  // φτάσουν, ξαναζωγραφίζεται η οθόνη (τα κουμπιά επιλογών της μπάρας/σύνοψης εξαρτώνται από αυτές).
  const groupsCache = useRef(new Map<number, PosOptionGroup[]>());
  const groupsInFlight = useRef(new Map<number, Promise<PosOptionGroup[]>>());
  const [, setGroupsVersion] = useState(0);
  const fetchGroups = useCallback((productId: number): Promise<PosOptionGroup[]> => {
    const cached = groupsCache.current.get(productId);
    if (cached) return Promise.resolve(cached);
    let pending = groupsInFlight.current.get(productId);
    if (!pending) {
      // Promise.resolve: ό,τι κι αν επιστρέψει η κλήση (και όχι-promise) — όπως το await πριν.
      pending = Promise.resolve(dineInApi.productOptionGroups(productId))
        .then((fetched) => {
          // Απάντηση που δεν είναι λίστα = καμία ομάδα (όχι σφάλμα στο πάτημα).
          const groups = Array.isArray(fetched) ? fetched : [];
          groupsCache.current.set(productId, groups);
          setGroupsVersion((v) => v + 1);
          return groups;
        })
        .finally(() => groupsInFlight.current.delete(productId));
      groupsInFlight.current.set(productId, pending);
    }
    return pending;
  }, []);
  const nextUid = useRef((restored?.lines.length ?? 0) + 1);
  const nextTouch = useRef((restored?.lines.length ?? 0) + 1);
  const contentRef = useRef<HTMLDivElement>(null);
  const pagerRef = useRef<HTMLDivElement>(null);
  /** Σελίδα-στόχος μιας κύλισης από πάτημα, ώσπου να φτάσει (βλ. onPagerScroll). */
  const scrollTarget = useRef<Page | null>(null);
  // Ρυθμίσεις συσκευής (γρανάζι): στήλες/μέγεθος καρτών, προϊόντα ως κάρτες ή λίστα, αναζήτηση.
  const prefs = useWaiterPrefs();
  const [settingsOpen, setSettingsOpen] = useState(false);

  const topCategories = useMemo(
    () => categories.filter((c) => !c.parent_id && !c.is_hidden && !c.is_system),
    [categories]
  );

  // Η επιλεγμένη κατηγορία όπως είναι ΤΩΡΑ στο μενού (ταίριασμα με ταυτότητα: κάθε ανανέωση φέρνει νέα
  // αντικείμενα). Χάθηκε (24/10/2026: «Καθαρισμός ημέρας» → τα «Πιάτα ημέρας» δεν φαίνονται πια, ενώ ο
  // σερβιτόρος ήταν μέσα τους και έβλεπε «κανένα προϊόν»): πίσω στην αρχή, όπως ανοίγει η οθόνη («Διάλεξε
  // κατηγορία»)· χάθηκε μόνο η υποκατηγορία → η κατηγορία της.
  const liveCategory = useMemo(
    () => (category ? topCategories.find((c) => c.id === category.id) ?? null : null),
    [category, topCategories]
  );
  useEffect(() => {
    if (!category || topCategories.length === 0) return;
    if (!liveCategory) {
      setCategory(null);
      setSubcategory(null);
    } else if (subcategory && !visibleSubcategories(liveCategory).some((s) => s.id === subcategory.id)) {
      setSubcategory(null);
    }
  }, [category, subcategory, liveCategory, topCategories.length]);

  // Νέα κατηγορία = από την κορυφή της λίστας, όχι από όπου είχε μείνει η προηγούμενη.
  useEffect(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [category, subcategory]);

  // Αναζήτηση κλειστή από το γρανάζι ενώ ήταν ανοιχτή: πίσω στον πίνακα κατηγοριών.
  useEffect(() => {
    if (!prefs.showSearch) {
      setSearch('');
      setSearchOpen(false);
    }
  }, [prefs.showSearch]);

  /** Βέλος πίσω (ή άδειασμα της σύνοψης): η σελίδα αλλάζει αμέσως, η οθόνη γλιστρά όπως στο σύρσιμο. */
  const goTo = useCallback((target: Page) => {
    // Η εστίαση δεν μένει σε κουμπί της σελίδας που κρύβεται.
    (document.activeElement as HTMLElement | null)?.blur?.();
    setPage(target);
    const pager = pagerRef.current;
    if (!pager) return;
    const left = target * pager.clientWidth;
    if (Math.abs(pager.scrollLeft - left) < 1) return;
    const smooth = !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (smooth && typeof pager.scrollTo === 'function') {
      // Μόνο η ομαλή κίνηση περνά από ενδιάμεσες θέσεις — μόνο τότε χρειάζεται ο φρουρός.
      scrollTarget.current = target;
      pager.scrollTo({ left, behavior: 'smooth' });
    } else {
      pager.scrollLeft = left;
    }
  }, []);

  /** Σύρσιμο: η σελίδα αλλάζει όταν η κύλιση «κουμπώσει» σε σελίδα (scroll-snap), όχι στη μέση της
   *  κίνησης. Όσο γλιστρά μετά από πάτημα, οι ενδιάμεσες θέσεις αγνοούνται — αλλιώς η αρχή της
   *  κίνησης («ακόμη στα προϊόντα») θα γύριζε πίσω τη σελίδα που μόλις άλλαξε. */
  const onPagerScroll = () => {
    const pager = pagerRef.current;
    if (!pager || !pager.clientWidth) return;
    const position = pager.scrollLeft / pager.clientWidth;
    if (scrollTarget.current !== null) {
      if (Math.abs(position - scrollTarget.current) <= 0.02) scrollTarget.current = null;
      return;
    }
    const snapped = Math.round(position);
    if (Math.abs(position - snapped) > 0.02) return;
    const target: Page = snapped >= 1 ? 1 : 0;
    if (target !== page) {
      (document.activeElement as HTMLElement | null)?.blur?.();
      setPage(target);
    }
  };

  /** Το δάχτυλο (ή ο τροχός) πάνω στις σελίδες: από εδώ αποφασίζει το σύρσιμο, όχι το πάτημα. */
  const releaseScroll = () => {
    scrollTarget.current = null;
  };

  const visibleProducts = useMemo(() => {
    const available = products.filter((p) => p.is_available !== false && !hiddenProductIds?.has(p.id));
    const term = search.trim().toLowerCase();
    if (term) return available.filter((p) => p.name.toLowerCase().includes(term)).slice(0, 60);
    const activeId = subcategory?.id ?? category?.id;
    if (!activeId) return [];
    return available.filter((p) => p.category_id === activeId);
  }, [products, hiddenProductIds, search, category, subcategory]);

  const cartCount = cart.reduce((s, l) => s + l.quantity, 0);
  const cartTotal = cart.reduce((s, l) => s + l.unit_price * l.quantity, 0);

  /** Πόσα έχουν μπει από κάθε είδος (όλα τα μεγέθη/επιλογές/πιάτα μαζί) — ο μετρητής του πλακιδίου. */
  const inCartByProduct = useMemo(() => {
    const counts = new Map<number, number>();
    for (const l of cart) counts.set(l.product_id, (counts.get(l.product_id) ?? 0) + l.quantity);
    return counts;
  }, [cart]);

  /** Το ίδιο ανά κατηγορία του πίνακα (μαζί με τις υποκατηγορίες της) — ο μετρητής της κάρτας. */
  const inCartByCategory = useMemo(() => {
    const topOf = new Map<number, number>();
    for (const top of topCategories) {
      topOf.set(top.id, top.id);
      for (const sub of top.subcategories ?? []) topOf.set(sub.id, top.id);
    }
    const categoryOf = new Map(products.map((p) => [p.id, p.category_id] as [number, number | null | undefined]));
    const counts = new Map<number, number>();
    for (const l of cart) {
      const top = topOf.get(Number(categoryOf.get(l.product_id)));
      if (top !== undefined) counts.set(top, (counts.get(top) ?? 0) + l.quantity);
    }
    return counts;
  }, [cart, products, topCategories]);

  // Έξοδος με είδη που δεν στάλθηκαν: πρώτο ✕ = προειδοποίηση, δεύτερο μέσα σε 4″ = έξοδος.
  // Με τον πίνακα κατηγοριών το ✕ κλείνει πλέον κατευθείαν — ένα λάθος άγγιγμα θα έσβηνε τη σύνοψη.
  const [closeArmed, setCloseArmed] = useState(false);
  useEffect(() => {
    if (!closeArmed) return;
    const timer = setTimeout(() => setCloseArmed(false), 4000);
    return () => clearTimeout(timer);
  }, [closeArmed]);

  const addLine = (product: Product, size: ProductSize | null, custom?: CustomizeResult) => {
    const options = custom?.options ?? [];
    const removed = custom?.removed ?? [];
    // Τιμή μονάδας = τιμή μεγέθους (Φ5: lineUnitPrice) + Σ επιλογών (utils/dineInMoney — ίδια με το
    // ταμείο), στο λεπτό.
    const unitPrice = roundToCents(unitPriceWithAddOns(lineUnitPrice(product, size, 'dine_in'), options));
    const key = lineKey(product.id, size?.id ?? null, options, removed);
    // Κάθε νέο είδος μπαίνει «Πρώτα»· το πιάτο αλλάζει στη σύνοψη. Ενώνεται μόνο με ίδια γραμμή
    // που είναι ακόμη «Πρώτα» — όχι με μία που ο σερβιτόρος έστειλε ήδη σε άλλο πιάτο.
    const fresh = { key, course_number: FIRST_COURSE, unit_price: unitPrice, quantity: 1, notes: null };
    const touched = nextTouch.current++;

    setCart((prev) => {
      const idx = prev.findIndex((l) => sameLine(l, fresh));
      if (idx !== -1) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + 1, touched };
        return next;
      }
      return [
        ...prev,
        {
          uid: nextUid.current++,
          key,
          product_id: product.id,
          size_id: size?.id ?? null,
          product_name: product.name,
          size_name: size?.name ?? null,
          unit_price: unitPrice,
          quantity: 1,
          notes: null,
          course_number: FIRST_COURSE,
          selected_options: options,
          removed,
          touched,
        },
      ];
    });
    setSizePicker(null);
    setCustomizing(null);
  };

  /** Γραμμή ανοιχτής τιμής ή ζυγίσματος (Ε9α): ποτέ συγχώνευση με άλλη — κάθε μία έχει δικό της ποσό
   *  (κλειδί -c<seq>). Μπαίνει «Πρώτα» όπως όλα (το πιάτο αλλάζει στη σύνοψη), χωρίς επιλογές, και
   *  γίνεται το «τελευταίο είδος» της μπάρας. */
  const addCustomLine = (
    product: Product,
    size: ProductSize | null,
    fields: Pick<CartLine, 'unit_price' | 'weight' | 'weight_unit' | 'is_open_price'>
  ) => {
    customLineSeq.current += 1;
    const line: CartLine = {
      uid: nextUid.current++,
      key: customLineKey(lineKey(product.id, size?.id ?? null, [], []), customLineSeq.current),
      product_id: product.id,
      size_id: size?.id ?? null,
      product_name: product.name,
      size_name: size?.name ?? null,
      quantity: 1,
      notes: null,
      course_number: FIRST_COURSE,
      selected_options: [],
      removed: [],
      touched: nextTouch.current++,
      ...fields,
    };
    setCart((prev) => [...prev, line]);
  };

  // Όπως η κονσόλα dine-in: το ποσό που πληκτρολογεί ο σερβιτόρος, στο προεπιλεγμένο μέγεθος.
  const addOpenPriceLine = (product: Product, price: number) => {
    addCustomLine(product, defaultSize(product.sizes), {
      unit_price: Math.round(price * 100) / 100,
      is_open_price: true,
    });
    setOpenPriceFor(null);
  };

  // Μία γραμμή, ποσότητα 1, ποσό = τιμή κιλού × βάρος με ΜΙΑ στρογγυλοποίηση (§3.7).
  const addWeightLine = (product: Product, weight: number) => {
    addCustomLine(product, defaultSize(product.sizes), {
      unit_price: weightedLineTotal(weightPricePerUnit(product), weight),
      weight,
      weight_unit: (product.selling_unit || 'KG').toUpperCase(),
    });
    setWeightFor(null);
  };

  /** Μετά το μέγεθος: οι υποχρεωτικές επιλογές, αν έχει το είδος — αλλιώς κατευθείαν στη σύνοψη. */
  const chooseSize = (product: Product, size: ProductSize | null) => {
    void openCustomization(product, size);
  };

  /** Προσθήκη (ιδιοκτήτης 7/10/2026): φύλλο ΜΟΝΟ με τις υποχρεωτικές ομάδες (π.χ. ψήσιμο, ζάχαρη)·
   *  οι προαιρετικές, το «χωρίς» και η σημείωση αλλάζουν μετά, από τη μπάρα. Οι προαιρετικές μπαίνουν με
   *  τις προεπιλογές τους (is_default) — ό,τι έβγαζε και το φύλλο με «Προσθήκη» χωρίς αλλαγή. */
  const openCustomization = async (product: Product, size: ProductSize | null) => {
    setSizePicker(null);
    let groups = groupsCache.current.get(product.id);
    if (!groups) {
      setLoadingProductId(product.id);
      try {
        groups = await fetchGroups(product.id);
      } catch (error) {
        // ΟΧΙ σιωπηλή προσθήκη χωρίς επιλογές: μια υποχρεωτική ομάδα (π.χ. ψήσιμο) θα χανόταν.
        console.error('Failed to load option groups:', error);
        showToast('error', 'Δεν φορτώθηκαν οι επιλογές του είδους — δοκίμασε ξανά.');
        return;
      } finally {
        setLoadingProductId(null);
      }
    }
    const usable = usableGroups(groups);
    const required = usable.filter((g) => g.is_required);
    const optionalDefaults = defaultSelections(usable.filter((g) => !g.is_required));
    if (required.length === 0) {
      // Γρήγορη προσθήκη (από τη 2η φορά χωρίς αναμονή δικτύου).
      addLine(product, size, { options: optionalDefaults, removed: [] });
      return;
    }
    setCustomizing({ product, size, groups: required, removable: [], allGroups: usable, extraOptions: optionalDefaults });
  };

  /** Αλλαγή γραμμής από τη μπάρα ή τη σύνοψη: όλες οι επιλογές του είδους (και η υποχρεωτική — έτσι
   *  διορθώνεται ένα λάθος ψήσιμο) ή μόνο το «χωρίς». */
  const openEdit = (line: CartLine, part: 'options' | 'removed') => {
    const custom = isCustomLine(line);
    // Ανοιχτή τιμή / ζύγισμα: καμία επιλογή — η αλλαγή θα ξαναϋπολόγιζε το ποσό από τον κατάλογο.
    if (custom && part === 'options') return;
    const product = products.find((p) => p.id === line.product_id);
    const size = product?.sizes?.find((s) => s.id === line.size_id) ?? null;
    // Χωρίς το είδος/μέγεθος δεν βγαίνει σωστή τιμή — καμία αλλαγή στα τυφλά.
    if (!product || (line.size_id !== null && !size)) {
      showToast('error', 'Το είδος δεν βρέθηκε στον κατάλογο — αφαίρεσέ το και βάλ’ το ξανά.');
      return;
    }
    setCustomizing({
      product,
      size,
      groups: part === 'options' ? usableGroups(groupsCache.current.get(product.id)) : [],
      removable: part === 'removed' ? removableSimpleIngredients(product.simple_ingredients) : [],
      edit: {
        uid: line.uid,
        part,
        quantity: line.quantity,
        options: line.selected_options,
        removed: line.removed.map((r) => r.id),
        ...(custom ? { fixedUnitPrice: line.unit_price } : {}),
      },
    });
  };

  /** Αποθήκευση αλλαγής: νέο κλειδί (οι επιλογές είναι μέσα του) και — μόνο αν άλλαξαν οι επιλογές —
   *  νέα τιμή μονάδας με τον ΙΔΙΟ τύπο της προσθήκης (utils/dineInMoney). «Μόνο σε 1» = ένα τεμάχιο
   *  γίνεται δική του γραμμή από κάτω και «τελευταίο είδος» (όπως η σημείωση).
   *  Ανοιχτή τιμή / ζύγισμα: μόνο το «χωρίς» αλλάζει· το ποσό μένει όπως είναι και το κλειδί παίρνει νέο
   *  -c<seq> (μοναδικό — αλλιώς ένα «Μόνο σε 1» θα ενωνόταν ξανά με το υπόλοιπο στην αποστολή). */
  const applyEdit = (c: Customizing, result: CustomizeResult, scope: EditScope) => {
    const edit = c.edit;
    if (!edit) return;
    const uid = nextUid.current++;
    const touched = nextTouch.current++;
    // Έξω από τον updater (StrictMode τον καλεί δύο φορές)· κενά στην αρίθμηση δεν πειράζουν.
    const customSeq = ++customLineSeq.current;
    setCart((prev) => {
      const idx = prev.findIndex((l) => l.uid === edit.uid);
      if (idx === -1) return prev;
      const line = prev[idx];
      const custom = isCustomLine(line);
      const options = edit.part === 'options' && !custom ? result.options : line.selected_options;
      const removed = edit.part === 'removed' ? result.removed : line.removed;
      const key = lineKey(line.product_id, line.size_id, options, removed);
      const changed: CartLine = {
        ...line,
        selected_options: options,
        removed,
        key: custom ? customLineKey(key, customSeq) : key,
        unit_price:
          edit.part === 'options' && !custom
            ? roundToCents(unitPriceWithAddOns(lineUnitPrice(c.product, c.size, 'dine_in'), options))
            : line.unit_price,
      };
      if (scope === 'one' && line.quantity > 1) {
        const next = [...prev];
        next.splice(idx, 1, { ...line, quantity: line.quantity - 1 }, { ...changed, uid, quantity: 1, touched });
        return next;
      }
      return prev.map((l) => (l.uid === line.uid ? changed : l));
    });
    setCustomizing(null);
  };

  /** Κουμπί επιλογών για μια γραμμή: το όνομα της ομάδας όταν είναι μία (π.χ. «ΖΥΜΑΡΙΚΟ», «ΨΗΣΙΜΟ»),
   *  «Επιλογές» για περισσότερες, τίποτα χωρίς ομάδες (ή όσο δεν έχουν φορτωθεί) — και ποτέ σε γραμμή
   *  ανοιχτής τιμής/ζυγίσματος (το ποσό της δεν ξαναϋπολογίζεται). */
  const optionsLabelFor = (line: CartLine): string | null => {
    if (isCustomLine(line)) return null;
    const groups = usableGroups(groupsCache.current.get(line.product_id));
    return groups.length === 0 ? null : groups.length === 1 ? groups[0].name : 'Επιλογές';
  };
  const canRemoveFor = (productId: number): boolean => {
    const product = products.find((p) => p.id === productId);
    return !!product && removableSimpleIngredients(product.simple_ingredients).length > 0;
  };

  const handleProductTap = (product: Product) => {
    if (loadingProductId !== null) return;
    // Πριν τα μεγέθη, όπως στο ταμείο: αλλιώς η γραμμή έφευγε με την τιμή καταλόγου (0,00).
    if (product.is_open_price) {
      setOpenPriceFor(product);
      return;
    }
    if (product.is_weight_variable) {
      setWeightFor(product);
      return;
    }
    // Ίδιος κανόνας με την κονσόλα dine-in (dineInQuickAdd): επιλογή μόνο με >1 πωλήσιμα μεγέθη,
    // αλλιώς ένα πάτημα με το προεπιλεγμένο (Φ5 — χωρίς «Κανονικό»).
    const quick = dineInQuickAdd(product);
    if (quick.pick) {
      setSizePicker(product);
      return;
    }
    chooseSize(product, quick.size);
  };

  const changeQuantity = (uid: number, delta: number) => {
    setCart((prev) =>
      prev
        .map((l) => (l.uid === uid ? { ...l, quantity: l.quantity + delta } : l))
        .filter((l) => l.quantity > 0)
    );
  };

  const removeLine = (uid: number) => {
    setCart((prev) => prev.filter((l) => l.uid !== uid));
    setExpandedUid((current) => (current === uid ? null : current));
  };

  // Γρήγορη διαγραφή με σύρσιμο: η γραμμή φεύγει αμέσως, «Αναίρεση» για λίγα δευτερόλεπτα (ένα
  // λάθος σύρσιμο δεν θέλει ξαναπέρασμα με επιλογές/σημειώσεις).
  const [undo, setUndo] = useState<{ line: CartLine; index: number } | null>(null);
  useEffect(() => {
    if (!undo) return;
    const timer = setTimeout(() => setUndo(null), 5000);
    return () => clearTimeout(timer);
  }, [undo]);

  const swipeDelete = (line: CartLine) => {
    setUndo({ line, index: Math.max(0, cart.findIndex((l) => l.uid === line.uid)) });
    removeLine(line.uid);
  };

  const undoDelete = () => {
    if (!undo) return;
    const { line, index } = undo;
    setCart((prev) => {
      if (prev.some((l) => l.uid === line.uid)) return prev;
      const next = [...prev];
      next.splice(Math.min(index, next.length), 0, line);
      return next;
    });
    setUndo(null);
  };

  /** Ένα πάτημα στο πιάτο: Πρώτα → Δεύτερα → Τρίτα → Πρώτα. Η γραμμή μένει στη θέση της και το
   *  κλειδί της δεν αλλάζει (βλ. lineKey). */
  const cycleCourse = (uid: number) => {
    setCart((prev) => prev.map((l) => (l.uid === uid ? { ...l, course_number: nextCourse(l.course_number) } : l)));
  };

  const openNotes = (line: CartLine) => {
    setNotesFor(line);
    setNotesDraft(line.notes || '');
  };

  /** Σημείωση σε όλη τη γραμμή — ή, όταν η γραμμή έχει πάνω από ένα, «μόνο σε 1»: ένα τεμάχιο γίνεται
   *  δική του γραμμή ακριβώς από κάτω (ίδιο είδος/επιλογές/πιάτο/τιμή — το σύνολο ΔΕΝ αλλάζει) και
   *  γίνεται το «τελευταίο είδος» της μπάρας. Π.χ. 2 TAGLIATA, η μία καλοψημένη. */
  const saveNotes = (scope: 'all' | 'one' = 'all') => {
    if (!notesFor) return;
    const value = notesDraft.trim() || null;
    const uid = nextUid.current++;
    const touched = nextTouch.current++;
    // Έξω από τον updater (StrictMode τον καλεί δύο φορές)· κενά στην αρίθμηση δεν πειράζουν.
    const customSeq = ++customLineSeq.current;
    setCart((prev) => {
      const idx = prev.findIndex((l) => l.uid === notesFor.uid);
      if (idx === -1) return prev;
      const line = prev[idx];
      if (scope === 'one' && value && line.quantity > 1) {
        // Ανοιχτή τιμή: το τεμάχιο που σπάει παίρνει δικό του -c<seq>, όπως στο applyEdit — δύο γραμμές
        // ανοιχτής τιμής δεν μοιράζονται ποτέ κλειδί (ούτε ενώνονται στην αποστολή).
        const key = isCustomLine(line)
          ? customLineKey(lineKey(line.product_id, line.size_id, line.selected_options, line.removed), customSeq)
          : line.key;
        const next = [...prev];
        next.splice(idx, 1, { ...line, quantity: line.quantity - 1 }, { ...line, uid, key, quantity: 1, notes: value, touched });
        return next;
      }
      return prev.map((l) => (l.uid === line.uid ? { ...l, notes: value } : l));
    });
    setNotesFor(null);
    setNotesDraft('');
  };

  /** Πάτημα σε κάρτα του πίνακα: αυτή η κατηγορία, από την αρχή της (και η ήδη επιλεγμένη = έξοδος
   *  από την υποκατηγορία). */
  const selectCategory = (cat: Category) => {
    setSearch('');
    setSearchOpen(false);
    setCategory(cat);
    setSubcategory(null);
  };

  const closeSearch = () => {
    setSearch('');
    setSearchOpen(false);
  };

  /** ✕ της οθόνης: αναζήτηση → υποκατηγορία → έξοδος (με επιβεβαίωση αν υπάρχουν είδη που δεν στάλθηκαν). */
  const handleClose = () => {
    if (searchOpen || search) { closeSearch(); return; }
    if (subcategory) { setSubcategory(null); return; }
    if (cartCount > 0 && !closeArmed) {
      setCloseArmed(true);
      showToast(
        'warning',
        `${cartCount} ${cartCount === 1 ? 'είδος δεν έχει' : 'είδη δεν έχουν'} σταλεί — πάτα ξανά ✕ για έξοδο χωρίς αποστολή.`
      );
      return;
    }
    // Επιβεβαιωμένη έξοδος χωρίς αποστολή = ο σερβιτόρος πετά το καλάθι· δεν μένει πρόχειρο.
    clearOrderDraft(storeId, table.id);
    onClose();
  };

  // Η σύνοψη άδειασε (διαγραφές ή «−»): τίποτα να δεις εκεί — πίσω στα προϊόντα. Όχι όσο φαίνεται
  // ακόμη η «Αναίρεση» μιας γρήγορης διαγραφής (θα χανόταν μαζί με τη σελίδα).
  useEffect(() => {
    if (cart.length === 0 && page === 1 && !undo) goTo(0);
  }, [cart.length, page, goTo, undo]);

  // Κάθε αλλαγή του καλαθιού φυλάγεται στη συσκευή (orderDraft): ένα refresh ή κλείσιμο της εφαρμογής
  // δεν χάνει την παραγγελία. Άδειο καλάθι = κανένα πρόχειρο (και σβήνει ένα παλιό που δεν ταίριαξε).
  useEffect(() => {
    if (cart.length === 0) {
      clearOrderDraft(storeId, table.id);
      return;
    }
    writeOrderDraft(storeId, {
      tableId: table.id,
      tableNumber: String(table.table_number),
      sessionId,
      guestCount,
      lines: cart,
      messages: orderMessages,
    });
  }, [cart, orderMessages, storeId, table.id, table.table_number, sessionId, guestCount]);

  const announced = useRef(false);
  useEffect(() => {
    // Μία φορά, στο άνοιγμα (και όχι δύο στο StrictMode του dev).
    if (!restored || announced.current) return;
    announced.current = true;
    const count = restored.lines.reduce((s, l) => s + l.quantity, 0);
    const at = new Date(restored.savedAt).toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit' });
    showToast('info', `Συνεχίζεις την παραγγελία που δεν στάλθηκε (${at}): ${count} ${count === 1 ? 'είδος' : 'είδη'}.`);
  }, [restored, showToast]);

  /** Το «τελευταίο είδος» της μπάρας: η γραμμή με την πιο πρόσφατη προσθήκη. Αν φύγει (π.χ. «−» ως
   *  το 0), η μπάρα δείχνει την προηγούμενη. */
  const lastLine = cart.reduce<CartLine | null>((last, l) => (!last || l.touched > last.touched ? l : last), null);

  // Τα κουμπιά επιλογών (μπάρα, ανοιχτή γραμμή σύνοψης) θέλουν τις ομάδες του είδους· μετά από πρόχειρο ή
  // για είδος που μπήκε πριν φορτωθούν, έρχονται εδώ — σιωπηλά (χωρίς ομάδες = απλώς χωρίς κουμπί).
  // Γραμμή ανοιχτής τιμής/ζυγίσματος δεν έχει κουμπί επιλογών: καμία κλήση για αυτήν.
  const expandedLine = cart.find((l) => l.uid === expandedUid);
  const expandedProductId = expandedLine && !isCustomLine(expandedLine) ? expandedLine.product_id : undefined;
  const lastProductId = lastLine && !isCustomLine(lastLine) ? lastLine.product_id : undefined;
  useEffect(() => {
    for (const id of [lastProductId, expandedProductId]) {
      if (typeof id === 'number' && !groupsCache.current.has(id)) {
        fetchGroups(id).catch(() => { /* ξαναδοκιμάζει στο επόμενο άνοιγμα */ });
      }
    }
  }, [lastProductId, expandedProductId, fetchGroups]);

  const lineShowsSize = (line: CartLine) =>
    !!line.size_name && showSizeName(products, line.product_id, { id: line.size_id, name: line.size_name });

  const send = async () => {
    // Το ref πιάνει και δεύτερο πάτημα πριν απενεργοποιηθεί το κουμπί (αλλιώς δεύτερη παραγγελία — βλ. ταμείο).
    if (cart.length === 0 || sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);
    try {
      const items = mergeCartLines(cart).map((l) => ({
        product_id: l.product_id,
        size_id: l.size_id,
        quantity: l.quantity,
        unit_price: l.unit_price,
        product_name: l.product_name,
        size_name: l.size_name,
        course_number: l.course_number,
        // Ίδιο σχήμα με την οθόνη τραπεζιών: store_ingredient_id + ονόματα στην ίδια σειρά.
        removedIngredients: l.removed.map((r) => r.id),
        ...(l.removed.length > 0 ? { removedIngredientNames: l.removed.map((r) => r.name) } : {}),
        addedExtras: [],
        selected_options: l.selected_options,
        notes: l.notes,
        // Ζύγισμα / ανοιχτή τιμή (Ε9α): το κατώτατο του server κρίνει τη γραμμή με αυτά.
        weight: l.weight ?? null,
        weight_unit: l.weight_unit ?? null,
        is_open_price: l.is_open_price === true,
      }));
      let targetSession = sessionId;
      if (!targetSession) {
        // Με τον γύρο: αν θα κοβόταν, ο server δεν ανοίγει καν το τραπέζι.
        const created = await dineInApi.openSession(storeId, table.id, guestCount, items);
        targetSession = created.id;
      }
      await dineInApi.createOrder(storeId, targetSession as number, {
        items,
        // Μόνο όταν υπάρχουν: χωρίς μηνύματα το payload μένει ΑΚΡΙΒΩΣ όπως πριν.
        ...(orderMessages.length > 0 ? { kitchen_messages: orderMessages } : {}),
      });
      clearOrderDraft(storeId, table.id);
      showToast('success', 'Η παραγγελία στάλθηκε στην κουζίνα.');
      onSent();
    } catch (error) {
      // Το app δεν έχει έκπτωση: κάτω από το κατώτατο σημαίνει ότι οι τιμές του καταλόγου άλλαξαν
      // από τότε που φορτώθηκε. Νέο φόρτωμα, και ο σερβιτόρος ξαναπροσθέτει — καμία τιμή του
      // καλαθιού δεν αλλάζει μόνη της.
      const stale = error instanceof DineInApiError && error.code === 'discount_forbidden';
      if (stale) onCatalogStale?.();
      const message =
        error instanceof DineInApiError
          ? error.code === 'receipt_issued'
            ? 'Έχει ήδη εκδοθεί απόδειξη — δεν μπαίνει νέα παραγγελία.'
            : stale
              ? STALE_PRICES_MESSAGE
              : error.message
          : 'Η αποστολή απέτυχε.';
      showToast('error', message);
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  const heading = search
    ? 'Αναζήτηση'
    : subcategory?.name || category?.name || 'Κατηγορίες';

  // Υπότιτλος σύνοψης: πόσα είδη και — μόνο όταν η παραγγελία έχει πάνω από ένα πιάτο — πόσα ανά πιάτο.
  const courseCounts = COURSES.map((c, idx) => ({
    label: c.label,
    qty: cart.filter((l) => l.course_number === idx + 1).reduce((s, l) => s + l.quantity, 0),
  })).filter((c) => c.qty > 0);
  const itemsLabel = `${cartCount} ${cartCount === 1 ? 'είδος' : 'είδη'}`;
  const summarySubtitle = [
    itemsLabel,
    ...(coursesEnabled && courseCounts.length > 1 ? courseCounts.map((c) => `${c.label} ${c.qty}`) : []),
  ].join(' · ');
  // Στα προϊόντα, με είδη: πλήθος και σύνολο στην κεφαλίδα (όχι πια κουμπί σύνοψης κάτω — η σύνοψη είναι
  // σύρσιμο). Η κατηγορία φαίνεται ήδη τονισμένη στον πίνακα.
  const orderSubtitle = `${itemsLabel} · ${money(cartTotal)}`;

  const subcategories = visibleSubcategories(liveCategory ?? category);

  return (
    <>
      <FullScreen
        title={page === 1 ? `Τραπέζι ${table.table_number} · Σύνοψη` : `Τραπέζι ${table.table_number}`}
        subtitle={page === 1 ? summarySubtitle : cartCount > 0 ? orderSubtitle : heading}
        onClose={page === 1 ? () => goTo(0) : handleClose}
        backLabel={page === 1 ? 'Πίσω στα προϊόντα' : undefined}
        scroll={false}
        actions={
          page === 0 && (
            <IconButton label="Ρυθμίσεις συσκευής" onClick={() => setSettingsOpen(true)}>
              <Settings className="w-6 h-6" />
            </IconButton>
          )
        }
      >
        {/* Δύο σελίδες δίπλα-δίπλα (scroll-snap): σύρσιμο = native κίνηση, και η κάθε σελίδα κυλά
            κάθετα μόνη της. Χωρίς είδη δεν σύρεται (άδεια σύνοψη). Οι σελίδες κρατούν ΜΟΝΟ τον κάθετο
            άξονα (overscroll-y-contain): ένα «contain» και στον οριζόντιο θα έκοβε το σύρσιμο που
            ξεκινά πάνω τους. Ο πίνακας κατηγοριών ΚΑΘΟΛΟΥ: όταν δεν ξεχειλίζει, ένα «contain» εκεί θα
            έκοβε την κύλιση των προϊόντων που ξεκινά πάνω του (το λάθος της αρχικής, 7/10/2026). */}
        <div
          ref={pagerRef}
          data-testid="order-pager"
          data-page={page}
          onScroll={onPagerScroll}
          onTouchStart={releaseScroll}
          onPointerDown={releaseScroll}
          onWheel={releaseScroll}
          className={`h-full flex overflow-y-hidden snap-x snap-mandatory overscroll-x-contain wa-no-scrollbar ${
            cart.length > 0 ? 'overflow-x-auto' : 'overflow-x-hidden'
          }`}
        >
          <section
            data-testid="page-products"
            aria-label="Προϊόντα"
            aria-hidden={page !== 0 || undefined}
            className="w-full h-full shrink-0 snap-start flex flex-col"
          >
            <div ref={contentRef} className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-y-contain">
              {/* Πάνω, πάντα ορατός: πίνακας με όλες τις κατηγορίες (ή η αναζήτηση). Ψηλότερος από 45%
                  της οθόνης δεν γίνεται — με πολλές κατηγορίες κυλά μέσα του, τα προϊόντα κρατούν χώρο. */}
              <div className="sticky top-0 z-10 bg-surface-0 border-b border-line px-3 pt-2 pb-2">
                {searchOpen ? (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-t3" />
                    <input
                      autoFocus
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Αναζήτηση προϊόντος"
                      className="w-full h-[44px] pl-10 pr-11 rounded-xl bg-surface-1 text-t1 text-base border-2 border-line placeholder-t4 focus:outline-none focus:border-blue-500"
                    />
                    <button
                      onClick={closeSearch}
                      aria-label="Κλείσιμο αναζήτησης"
                      className="absolute right-0 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center text-t3"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                ) : (
                  /* Προεπιλογή 4 στήλες στο κινητό (στα 360px μένουν ~75px ανά κάρτα· το fitCategoryFont
                     μικραίνει τις μεγάλες λέξεις), 5 από 480px, 6 από 640px — αλλάζει από το γρανάζι.
                     Η αναζήτηση πρώτη κάρτα ΜΟΝΟ αν την ανοίξει η συσκευή (γρανάζι· προεπιλογή όχι).
                     Νέες στήλες/μέγεθος = νέο key: οι κάρτες ξαναμετρούν. */
                  <div
                    key={`${prefs.categoryColumns}-${prefs.categorySize}`}
                    role="group"
                    aria-label="Κατηγορίες"
                    className={`grid ${CATEGORY_GRID[prefs.categoryColumns]} gap-1 max-h-[45vh] overflow-y-auto overflow-x-hidden`}
                  >
                    {prefs.showSearch && (
                      <button
                        type="button"
                        onClick={() => setSearchOpen(true)}
                        aria-label="Αναζήτηση"
                        className={`${CATEGORY_CARD[prefs.categorySize].minH} px-1 py-1 rounded-lg border border-dashed border-line-strong bg-surface-0 text-t2 flex flex-col items-center justify-center gap-0.5 active:bg-surface-2`}
                      >
                        <Search className="w-4 h-4" />
                        <span className="text-[10px] font-bold leading-none">Αναζήτηση</span>
                      </button>
                    )}
                    {topCategories.map((cat) => (
                      <CategoryCard
                        key={cat.id}
                        category={cat}
                        active={category?.id === cat.id}
                        inCart={inCartByCategory.get(cat.id) ?? 0}
                        size={prefs.categorySize}
                        onSelect={selectCategory}
                      />
                    ))}
                  </div>
                )}
              </div>

              <div className="p-3">
                {/* Πριν διαλέξει κατηγορία: τίποτα να «ψάξει» — μόνο υπόδειξη. */}
                {!search && !category && (
                  <div className="py-8 text-center">
                    {topCategories.length === 0 ? (
                      <EmptyState icon={Package} title="Δεν υπάρχουν κατηγορίες" />
                    ) : (
                      <>
                        <ArrowUp className="w-8 h-8 mx-auto mb-2 text-t4" />
                        <p className="font-bold text-base text-t2">Διάλεξε κατηγορία</p>
                        {prefs.showSearch && <p className="text-sm text-t4 mt-0.5">ή ψάξε προϊόν με την «Αναζήτηση»</p>}
                      </>
                    )}
                  </div>
                )}

                {/* Μέσα σε υποκατηγορία: οι αδελφές της σε λωρίδα (π.χ. VODKA · WHISKEY · GIN). */}
                {!search && subcategory && subcategories.length > 1 && (
                  <div className="flex mb-2">
                    <ChipStrip label="Υποκατηγορίες" items={subcategories} activeId={subcategory.id} onSelect={setSubcategory} />
                  </div>
                )}

                {/* 2 στήλες στο κινητό, ΟΧΙ 3: στα 360–412px μένουν ~86–104px για κείμενο
                    και τα μεγάλα ελληνικά ονόματα («ΖΑΧΑΡΟΠΛΑΣΤΙΚΗ», «HANDROLLS ΓΑΡΙΔΑ
                    ΑΒΟΚΑΝΤΟ CHILI») κόβονταν στη μέση ή χάνανε τη λέξη που ξεχωρίζει το
                    είδος (κίνδυνος λάθους παραγγελίας). Η πυκνότητα έρχεται από το ύψος.
                    3 στήλες μόνο από 640px (ταμπλέτα / οριζόντια). */}
                {/* Υποκατηγορίες της επιλεγμένης κατηγορίας */}
                {!search && category && !subcategory && subcategories.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-2">
                    {subcategories.map((sub) => (
                      <button
                        key={sub.id}
                        onClick={() => setSubcategory(sub)}
                        className="role-mobile-card min-h-[64px] rounded-xl bg-surface-1 border-2 border-line p-2 flex items-center justify-center text-center active:bg-surface-2"
                      >
                        <span className="font-bold text-[15px] text-t1 leading-tight break-words max-w-full">{sub.name}</span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Προϊόντα — κάρτες ή λίστα (γρανάζι), με μετρητή όσων έχουν ήδη μπει στη σύνοψη */}
                {(search || category) && (
                  <div
                    data-testid="products"
                    data-view={prefs.productView}
                    className={
                      prefs.productView === 'list'
                        ? 'rounded-xl border border-line bg-surface-1 divide-y divide-line overflow-hidden'
                        : `grid ${PRODUCT_GRID[prefs.productColumns]} gap-2`
                    }
                  >
                    {visibleProducts.map((product) => {
                      const inCart = inCartByProduct.get(product.id) ?? 0;
                      const loading = loadingProductId === product.id;
                      const price = tilePrice(product);
                      const badge = inCart > 0 && (
                        <span
                          key={inCart}
                          data-testid={`in-cart-${product.id}`}
                          aria-label={`${inCart} στη σύνοψη`}
                          className={`wa-pop min-w-[24px] h-6 px-1.5 rounded-full bg-blue-600 text-white text-sm font-bold leading-6 text-center tabular-nums ${
                            prefs.productView === 'list' ? 'shrink-0' : 'absolute top-1 right-1'
                          }`}
                        >
                          {inCart}
                        </span>
                      );
                      if (prefs.productView === 'list') {
                        const row = PRODUCT_ROW[prefs.productSize];
                        return (
                          <button
                            key={product.id}
                            onClick={() => handleProductTap(product)}
                            aria-busy={loading || undefined}
                            className={`w-full ${row.minH} px-3 py-1.5 flex items-center gap-3 text-left active:bg-surface-2 ${
                              inCart > 0 ? 'bg-blue-500/10' : ''
                            }`}
                          >
                            <span className={`flex-1 min-w-0 font-bold text-t1 leading-tight break-words ${row.name}`}>{product.name}</span>
                            {badge}
                            {loading ? (
                              <Loader2 className="w-5 h-5 shrink-0 animate-spin text-blue-600 dark:text-blue-400" />
                            ) : (
                              <span className={`shrink-0 font-bold text-emerald-600 dark:text-emerald-400 tabular-nums ${row.price}`}>{price}</span>
                            )}
                          </button>
                        );
                      }
                      const card = PRODUCT_CARD[prefs.productSize];
                      return (
                        <button
                          key={product.id}
                          onClick={() => handleProductTap(product)}
                          aria-busy={loading || undefined}
                          className={`relative ${card.minH} rounded-xl bg-surface-1 border-2 p-2 flex flex-col items-center justify-center text-center active:bg-surface-2 ${
                            inCart > 0 ? 'border-blue-500' : 'border-line'
                          }`}
                        >
                          {badge}
                          <span className={`font-bold text-t1 leading-tight line-clamp-3 break-words max-w-full ${card.name}`}>
                            {product.name}
                          </span>
                          {loading ? (
                            <Loader2 className="mt-1 w-5 h-5 animate-spin text-blue-600 dark:text-blue-400" />
                          ) : (
                            <span className={`mt-1 font-bold text-emerald-600 dark:text-emerald-400 tabular-nums ${card.price}`}>{price}</span>
                          )}
                        </button>
                      );
                    })}
                    {visibleProducts.length === 0 && !(category && !subcategory && subcategories.length > 0 && !search) && (
                      <div className="col-span-full">
                        <EmptyState icon={Package} title="Κανένα προϊόν εδώ" />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="border-t border-line bg-surface-1 px-3 pt-2 shrink-0 wa-safe-bottom">
              <LastItemBar
                line={lastLine}
                showSize={!!lastLine && lineShowsSize(lastLine)}
                optionsLabel={lastLine ? optionsLabelFor(lastLine) : null}
                canRemove={!!lastLine && canRemoveFor(lastLine.product_id)}
                onOptions={() => lastLine && openEdit(lastLine, 'options')}
                onRemoved={() => lastLine && openEdit(lastLine, 'removed')}
                onNote={() => lastLine && openNotes(lastLine)}
                onRemoveOne={() => lastLine && changeQuantity(lastLine.uid, -1)}
              />
            </div>
          </section>

          {/* Σύνοψη: μία σειρά ανά είδος, πιάτο με ένα πάτημα, αποστολή με το σύνολο στο κουμπί */}
          <section
            data-testid="page-summary"
            aria-label="Σύνοψη"
            aria-hidden={page !== 1 || undefined}
            className="w-full h-full shrink-0 snap-start flex flex-col"
          >
            <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-y-contain">
              {cart.length === 0 ? (
                <EmptyState icon={ClipboardList} title="Δεν υπάρχουν είδη" />
              ) : (
                <>
                  <ul className="divide-y divide-line border-b border-line">
                    {cart.map((line) => (
                      <SummaryLine
                        key={line.uid}
                        line={line}
                        showSize={lineShowsSize(line)}
                        coursesEnabled={coursesEnabled}
                        expanded={expandedUid === line.uid}
                        optionsLabel={expandedUid === line.uid ? optionsLabelFor(line) : null}
                        canRemove={expandedUid === line.uid && canRemoveFor(line.product_id)}
                        onToggle={() => setExpandedUid((current) => (current === line.uid ? null : line.uid))}
                        onCourse={() => cycleCourse(line.uid)}
                        onQuantity={(delta) => changeQuantity(line.uid, delta)}
                        onNote={() => openNotes(line)}
                        onOptions={() => openEdit(line, 'options')}
                        onRemoved={() => openEdit(line, 'removed')}
                        onRemove={() => removeLine(line.uid)}
                        onSwipeDelete={() => swipeDelete(line)}
                      />
                    ))}
                  </ul>
                  <p className="px-3 py-2 text-[13px] text-t4 leading-snug">
                    {coursesEnabled
                      ? 'Πάτα το πιάτο για να γίνει Δεύτερα ή Τρίτα. Πάτα το είδος για ποσότητα, επιλογές, σημείωση ή διαγραφή.'
                      : 'Πάτα ένα είδος για ποσότητα, επιλογές, σημείωση ή διαγραφή.'}
                    {' Σύρε ένα είδος αριστερά για γρήγορη διαγραφή.'}
                  </p>
                </>
              )}
            </div>
            {undo && (
              <div
                role="status"
                className="shrink-0 mx-3 mb-2 pl-3 min-h-[48px] rounded-xl bg-slate-800 text-white flex items-center gap-2 shadow-lg"
              >
                <Trash2 className="w-4 h-4 shrink-0 text-red-300" />
                <span className="flex-1 min-w-0 truncate text-sm">
                  Διαγράφηκε: {undo.line.quantity}× {undo.line.product_name}
                </span>
                <button
                  type="button"
                  onClick={undoDelete}
                  className="shrink-0 min-h-[44px] px-3 rounded-xl text-sm font-bold text-amber-300 active:bg-white/10"
                >
                  Αναίρεση
                </button>
              </div>
            )}
            <div className="border-t border-line bg-surface-1 px-3 pt-2 shrink-0 wa-safe-bottom">
              <div className="space-y-2 pb-1">
                {kitchenMessages && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => setMessageSheet(true)}
                      className="min-h-[44px] px-3 rounded-xl bg-surface-2 border border-line-strong dark:border-transparent text-t1 text-sm font-bold flex items-center gap-2 active:bg-surface-3"
                    >
                      <MessageSquare className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                      Μήνυμα κουζίνας
                    </button>
                    {orderMessages.map((message) => (
                      <span key={message} className="inline-flex items-center pl-3 rounded-xl bg-teal-600 text-white text-sm font-bold">
                        {message}
                        <button
                          onClick={() => setOrderMessages((prev) => prev.filter((m) => m !== message))}
                          aria-label="Αφαίρεση μηνύματος"
                          className="w-[44px] h-[44px] flex items-center justify-center"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <Button tone="primary" full disabled={sending || cart.length === 0} onClick={send}>
                  {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
                  <span className="flex-1 text-left">Αποστολή στην κουζίνα</span>
                  <span className="tabular-nums">{money(cartTotal)}</span>
                </Button>
              </div>
            </div>
          </section>
        </div>
      </FullScreen>

      {/* Ρυθμίσεις συσκευής — και από εδώ, για να φαίνεται αμέσως το αποτέλεσμα */}
      {settingsOpen && <DeviceSettingsSheet onClose={() => setSettingsOpen(false)} />}

      {/* Επιλογή μεγέθους — μόνο τα μεγέθη, με την απόλυτη τιμή dine-in του καθενός (Φ5) */}
      {sizePicker && (
        <Sheet title={sizePicker.name} subtitle="Διάλεξε μέγεθος" onClose={() => setSizePicker(null)}>
          <div className="p-3 space-y-2">
            {sizePicker.sizes!.map((size) => (
              <button
                key={size.id}
                onClick={() => chooseSize(sizePicker, size)}
                className="w-full min-h-[52px] px-3 rounded-xl bg-surface-1 border-2 border-line flex items-center justify-between active:bg-surface-2"
              >
                <span className="font-bold text-base text-t1">{size.name}</span>
                <span className="font-bold text-lg text-emerald-600 dark:text-emerald-400 tabular-nums">
                  {money(lineUnitPrice(sizePicker, size, 'dine_in'))}
                </span>
              </button>
            ))}
          </div>
        </Sheet>
      )}

      {/* Ανοιχτή τιμή / ζύγισμα — τα ίδια πληκτρολόγια με το ταμείο */}
      {openPriceFor && (
        <AboveScreens>
          <OpenPriceModal
            product={openPriceFor}
            onConfirm={addOpenPriceLine}
            onClose={() => setOpenPriceFor(null)}
          />
        </AboveScreens>
      )}
      {weightFor && (
        <AboveScreens>
          <WeightModal
            product={weightFor}
            pricePerUnit={weightPricePerUnit(weightFor)}
            onConfirm={addWeightLine}
            onClose={() => setWeightFor(null)}
          />
        </AboveScreens>
      )}

      {/* Επιλογές / «χωρίς» */}
      {customizing && (
        <CustomizeSheet
          title={customizing.product.name}
          subtitle={
            customizing.size &&
            showSizeName(products, customizing.product.id, { id: customizing.size.id, name: customizing.size.name })
              ? customizing.size.name
              : undefined
          }
          // Ανοιχτή τιμή / ζύγισμα: το ποσό της γραμμής, όχι τιμή καταλόγου.
          basePrice={customizing.edit?.fixedUnitPrice ?? lineUnitPrice(customizing.product, customizing.size, 'dine_in')}
          groups={customizing.groups}
          removable={customizing.removable}
          mode={customizing.edit ? 'edit' : 'add'}
          initialOptions={customizing.edit?.options}
          initialRemoved={customizing.edit?.removed}
          quantity={customizing.edit?.quantity}
          // Επιλογές που μετρούν στην τιμή χωρίς να φαίνονται: στην προσθήκη οι προεπιλογές των
          // προαιρετικών, στο «χωρίς» οι επιλογές που ήδη έχει η γραμμή.
          priceOptions={
            customizing.edit
              ? customizing.edit.part === 'removed' ? customizing.edit.options : []
              : customizing.extraOptions
          }
          onClose={() => setCustomizing(null)}
          onConfirm={(result, scope) =>
            customizing.edit
              ? applyEdit(customizing, result, scope)
              : addLine(customizing.product, customizing.size, {
                options: inGroupOrder(customizing.allGroups ?? customizing.groups, [
                  ...result.options,
                  ...(customizing.extraOptions ?? []),
                ]),
                removed: [],
              })
          }
        />
      )}

      {/* Μηνύματα κουζίνας μαζί με την παραγγελία */}
      {messageSheet && kitchenMessages && (
        <KitchenMessageSheet
          tableNumber={table.table_number}
          presets={kitchenMessages}
          mode="attach"
          initial={orderMessages}
          onConfirm={(messages) => { setOrderMessages(messages); setMessageSheet(false); }}
          onClose={() => setMessageSheet(false)}
        />
      )}

      {/* Σημείωση γραμμής — με πάνω από ένα τεμάχιο: σε ένα ή σε όλα */}
      {notesFor && (
        <Sheet
          title="Σημείωση"
          subtitle={notesFor.quantity > 1 ? `${notesFor.quantity}× ${notesFor.product_name}` : notesFor.product_name}
          onClose={() => setNotesFor(null)}
          footer={
            <div className="pb-1 flex gap-2">
              {notesFor.quantity > 1 && notesDraft.trim() ? (
                <>
                  <Button tone="neutral" className="flex-1" onClick={() => saveNotes('one')}>
                    Μόνο σε 1
                  </Button>
                  <Button tone="primary" className="flex-1" onClick={() => saveNotes('all')}>
                    Σε όλα ({notesFor.quantity})
                  </Button>
                </>
              ) : (
                <Button tone="primary" full onClick={() => saveNotes('all')}>
                  Αποθήκευση
                </Button>
              )}
            </div>
          }
        >
          <div className="p-3">
            <textarea
              autoFocus
              value={notesDraft}
              onChange={(e) => setNotesDraft(e.target.value)}
              rows={3}
              placeholder="π.χ. χωρίς κρεμμύδι"
              className="w-full p-3 rounded-2xl bg-surface-2 dark:bg-surface-1 border border-line text-t1 text-base placeholder-t4 focus:outline-none focus:border-blue-500"
            />
          </div>
        </Sheet>
      )}
    </>
  );
};

export default OrderScreen;
